/* @jsx h */
import {
  computed,
  defineComponent,
  h,
  inject,
  onMounted,
  provide,
  ref,
  watch,
  type ComputedRef,
  type PropType,
  type Ref,
} from "vue";
import { useRouter } from "vue-router";
import type { User } from "@lemmonhq/types";
import { api } from "@app/data/api";
import { useNativeBridge } from "@app/vue/providers/NativeBridge";

export type GroupRole = "admin" | "viewer" | "employee";

export type IdentityContext = {
  identity_id: number;
  group_id: number;
  group_role: GroupRole;
  group_name: string;
};

export type PosSession = User & {
  mode: "pos";
  identity?: IdentityContext | null;
};

export type IdentitySessionShop = {
  id: number;
  guid: string;
  name: string;
  address: string;
  jurisdiction: string;
  status: string;
  image_url: string | null;
  user_id: number | null;
  pos_role: "staff" | "manager" | null;
  can_open_pos: boolean;
};

export type IdentitySession = {
  mode: "group";
  identity: {
    id: number;
    name: string;
    email: string;
    guid: string;
    status: string;
  };
  group: { id: number; guid: string; name: string };
  group_role: GroupRole;
  shops: IdentitySessionShop[];
};

export type RootSession = PosSession | IdentitySession;
export type AuthSession = PosSession;

export const isGroupSession = (
  session: RootSession | null | undefined,
): session is IdentitySession => session?.mode === "group";

export const isGroupRoute = (path: string) =>
  path === "/group" || path.startsWith("/group/");

/** The rota's personal staff links (/team/<token>) — no login, no POS shell. */
export const isTeamLinkRoute = (path: string) => path.startsWith("/team/");

export const getSessionRouteRedirect = (
  session: RootSession | null,
  path: string,
): string | null => {
  // A team member's personal rota link works whoever is signed in (or not).
  if (isTeamLinkRoute(path)) return null;
  if (isGroupSession(session)) {
    if (!isGroupRoute(path)) return "/group/dashboard";
    if (
      session.group_role !== "admin" &&
      (path.startsWith("/group/users") || path.startsWith("/group/settings"))
    ) {
      return "/group/dashboard";
    }
    return null;
  }

  return isGroupRoute(path) ? "/" : null;
};

type RootSessionContext = {
  session: Ref<RootSession | null>;
  loading: Ref<boolean>;
  refresh: () => Promise<RootSession | null>;
};

const RootSessionContextKey = Symbol("RootSessionContext");

export const RootSessionProvider = defineComponent({
  name: "RootSessionProvider",
  setup(_, { slots }) {
    const router = useRouter();
    const session = ref<RootSession | null>(null) as Ref<RootSession | null>;
    const loading = ref(true);

    const normalizeRoute = async (nextSession: RootSession | null) => {
      const redirect = getSessionRouteRedirect(
        nextSession,
        router.currentRoute.value.path,
      );
      if (redirect) await router.replace(redirect);
    };

    const refresh = async () => {
      const nextSession = (await api.getSession()) ?? null;
      await normalizeRoute(nextSession);
      session.value = nextSession;
      return nextSession;
    };

    onMounted(async () => {
      try {
        await refresh();
      } finally {
        loading.value = false;
      }
    });

    watch(
      () => router.currentRoute.value.path,
      () => {
        if (loading.value) return;
        void normalizeRoute(session.value);
      },
    );

    provide<RootSessionContext>(RootSessionContextKey, {
      session,
      loading,
      refresh,
    });

    return () =>
      loading.value ? (
        <main class="min-h-screen bg-base-100 flex items-center justify-center">
          <span class="loading loading-spinner loading-lg text-primary" />
        </main>
      ) : (
        slots.default?.()
      );
  },
});

export function useRootSession(): RootSessionContext {
  const context = inject<RootSessionContext>(RootSessionContextKey);
  if (!context) {
    throw new Error("useRootSession must be used within RootSessionProvider");
  }
  return context;
}

interface AuthContextType {
  user: Ref<AuthSession | null>;
  setUser: (user: User | AuthSession | null) => void;
  posMode: Ref<boolean>;
  refetchSession: () => Promise<RootSession | null | undefined>;
  identity: ComputedRef<IdentityContext | null>;
  groupRole: ComputedRef<GroupRole | null>;
}

const AuthContextKey = Symbol("AuthContext");

export const PosAuthProvider = defineComponent({
  name: "PosAuthProvider",
  props: {
    session: {
      type: Object as PropType<PosSession | null>,
      default: null,
    },
  },
  setup(props, { slots }) {
    const user = ref<AuthSession | null>(
      props.session,
    ) as Ref<AuthSession | null>;
    const posMode = ref(false);
    const bridge = useNativeBridge();

    const identify = (nextUser: AuthSession | null) => {
      if (!nextUser || !bridge) return;
      void bridge.identify({
        user_id: nextUser.id.toString(),
        shop_id: nextUser.restaurant_id.toString(),
        name: nextUser.profile.name,
        email: nextUser.profile.email,
        shop_name: nextUser.restaurant.name,
      });
    };

    const setUser = (nextUser: User | AuthSession | null) => {
      const normalized = nextUser
        ? ({ ...nextUser, mode: "pos" } as AuthSession)
        : null;
      user.value = normalized;
      identify(normalized);
    };

    const refetchSession = async () => {
      const nextSession = await api.getSession();
      if (nextSession && !isGroupSession(nextSession)) {
        setUser(nextSession);
      }
      return nextSession;
    };

    const identity = computed<IdentityContext | null>(
      () => user.value?.identity ?? null,
    );
    const groupRole = computed<GroupRole | null>(
      () => user.value?.identity?.group_role ?? null,
    );

    onMounted(() => identify(user.value));

    provide<AuthContextType>(AuthContextKey, {
      user,
      setUser,
      posMode,
      refetchSession,
      identity,
      groupRole,
    });

    return () => slots.default?.();
  },
});

// Compatibility alias for existing imports; all POS consumers remain narrowed.
export const AuthProvider = PosAuthProvider;

export function useAuth(): AuthContextType {
  const context = inject<AuthContextType>(AuthContextKey);
  if (!context) {
    throw new Error("useAuth must be used within PosAuthProvider");
  }
  return context;
}
